/* Rule suggestions for one dataset, over the real API. No `X-Lumiq-Tenant` header — hard rule 1.
 *
 * Two hooks and one mutation, and the mutation is a **PUT**. `ai-rulegen.yaml` explains why at
 * length: the first draft was a POST arguing it needed no `Idempotency-Key` because it is idempotent
 * by natural key, and the `post-idempotency-key` gate refused the build — correctly, since hard rule 7
 * does not take an argument. Regenerating *replaces* the dataset's suggestion set with what the
 * current schema justifies, so the verb was the honest answer rather than an exemption.
 *
 * `put` did not exist in `shared/api/http` and is added there rather than inlined here, so the next
 * PUT surface (the sample policy, `ai/serving/route`) has one to reach for instead of a second fetch
 * call with its own header spelling.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';

import {
  isGenerationOutcome,
  isRuleDraft,
  isRulegenCapabilities,
  isSuggestionCounts,
  isSuggestionList,
} from '@/shared/api/generated/ai-rulegen';
import type {
  GenerationOutcome,
  RuleDraft,
  RulegenCapabilities,
  SuggestionCounts,
  SuggestionList,
} from '@/shared/api/generated/ai-rulegen';
import { getOne, put } from '@/shared/api/http';

export const CAPABILITIES_URL = '/v1/ai/rulegen/capabilities';
export const COUNTS_URL = '/v1/ai/rulegen/suggestion-counts';

/* Re-exported so the screen and the presentation model import from one place, the same seam every
 * other feature slice keeps: this module is where a wire type enters the feature. */
export type {
  CandidateSource,
  DatasetSuggestionCount,
  GenerationOutcome,
  ModelStatus,
  RejectionReason,
  RuleDraft,
  RulegenCapabilities,
  Suggestion,
  SuggestionCounts,
  SuggestionList,
} from '@/shared/api/generated/ai-rulegen';

function suggestionsUrl(datasetId: string): string {
  return `/v1/ai/rulegen/datasets/${encodeURIComponent(datasetId)}/suggestions`;
}

export function useRulegenCapabilities(): UseQueryResult<RulegenCapabilities> {
  return useQuery({
    queryKey: ['rulegen', 'capabilities'],
    queryFn: () =>
      getOne(CAPABILITIES_URL, isRulegenCapabilities, 'rulegen capabilities'),
    /* Five minutes. The generator's own boundaries change when the deployment changes, not while a
     * steward is reading — and `never_proposed` is a security argument this screen renders, so it
     * should not blink between refetches. */
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

export function useSuggestions(
  datasetId: string | undefined,
): UseQueryResult<SuggestionList> {
  return useQuery({
    queryKey: ['rulegen', 'suggestions', datasetId],
    queryFn: () =>
      getOne(suggestionsUrl(datasetId ?? ''), isSuggestionList, 'suggestions'),
    enabled: datasetId !== undefined,
    retry: false,
  });
}

/** How many suggestions each dataset has. **One request for the estate, never one per row.**
 *
 * The alternative is a fetch per catalogue row, and an N+1 is why a badge like this gets cut from a
 * design rather than shipped. A dataset with no suggestions is *absent* from the answer rather than
 * present with `0` — the contract says why, and a consumer must read a missing key as "not
 * generated for" rather than as "generated and found nothing".
 *
 * Five minutes stale, matching `useRulegenCapabilities`: a badge is ambient information beside
 * something a reader came for, and one that flickers between refetches is worse than one slightly
 * behind. Regeneration invalidates it, so the screen that *caused* a change sees it at once.
 */
export function useSuggestionCounts(): UseQueryResult<SuggestionCounts> {
  return useQuery({
    queryKey: ['rulegen', 'counts'],
    queryFn: () => getOne(COUNTS_URL, isSuggestionCounts, 'suggestion counts'),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

/** The field values a rule form is prefilled with. **A GET, so it creates nothing.**
 *
 * §2's loop runs through this rather than through an accept route: a suggestion becomes a rule only
 * by a person submitting `POST /v1/rules`, and this hook supplies what that form starts with. The
 * draft carries `tags` holding `suggested-from:<id>`, which is what lets the *second* hop — the
 * version's columns and parameters — be recovered after a reload rather than surviving only in
 * router state.
 *
 * `enabled` on a present id, so `RuleDetail` may call it unconditionally for a rule that turns out
 * to carry no provenance tag. Never retried: a 404 means the suggestion was regenerated away, which
 * is a real answer and not a transient one.
 */
export function useRuleDraft(
  suggestionId: string | undefined,
): UseQueryResult<RuleDraft> {
  return useQuery({
    queryKey: ['rulegen', 'draft', suggestionId],
    queryFn: () =>
      getOne(
        `/v1/ai/rulegen/suggestions/${encodeURIComponent(suggestionId ?? '')}/rule-draft`,
        isRuleDraft,
        'rule draft',
      ),
    enabled: suggestionId !== undefined && suggestionId !== '',
    retry: false,
  });
}

/** Regenerates, and returns the outcome — which carries what the plain list cannot.
 *
 * `GenerationOutcome` holds `by_source`, `rejected`, `columns_considered`,
 * `unbounded_numeric_columns` and the run's `model_status`; `SuggestionList` holds only the
 * suggestions. That asymmetry is the contract's and it is right — those numbers describe *a run*,
 * not a dataset's current state, so serving them from the GET would attribute the last
 * generation's counts to whatever the list holds now, and would report "AI contributed" for a run
 * some earlier caller performed. The screen therefore keeps the outcome from the regeneration it
 * performed and does not pretend to have one before.
 */
export function useRegenerateSuggestions(): UseMutationResult<
  GenerationOutcome,
  Error,
  string
> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (datasetId: string) =>
      put(suggestionsUrl(datasetId), isGenerationOutcome, 'generation outcome', {}),
    onSuccess: (outcome) => {
      void client.invalidateQueries({
        queryKey: ['rulegen', 'suggestions', outcome.dataset_id],
      });
      /* The badge count is stale for five minutes by design, and a regeneration is the one event
         that must not wait: a reader who has just generated and sees "3 suggested" beside a dataset
         that now has seven would reasonably conclude the run did not take. */
      void client.invalidateQueries({ queryKey: ['rulegen', 'counts'] });
    },
    retry: false,
  });
}
